// Operator-defined 3-zone alerting (2026-05-18):
//   Healthy → stock has not reached the warning threshold yet
//   Green Zone → 80% of stock buffer consumed (early reorder signal)
//   Red Zone   → 90% consumed (action needed)
//   Black Zone → 100% consumed (depleted; reorder NOW)
//
// Reference: maxStockLevel (the operator-set buffer ceiling) if present,
// otherwise reorderLevel. If neither configured, item is considered Healthy
// (no alerting until buffer is defined).
//
// Replaces the prior 5-tier (Green/Yellow/Orange/Red/Black) system.

export type AlertTier = "Healthy" | "Green" | "Red" | "Black";

export interface AlertInput {
  currentStock: number | string | null;
  maxStockLevel?: number | string | null;
  reorderLevel?: number | string | null;
  safetyStock?: number | string | null;
}

export function computeAlertTier(item: AlertInput): AlertTier {
  const stock = Number(item.currentStock ?? 0);
  const max = Number(item.maxStockLevel ?? item.reorderLevel ?? 0);
  if (max <= 0) return "Healthy";
  if (stock <= 0) return "Black";
  const consumedPct = (max - stock) / max;
  if (consumedPct >= 1.0) return "Black";
  if (consumedPct >= 0.9) return "Red";
  if (consumedPct >= 0.8) return "Green";
  return "Healthy";
}

export const ALERT_TIER_COLORS: Record<AlertTier, string> = {
  Healthy: "bg-zinc-100 text-zinc-600 border-zinc-200",
  Green:   "bg-green-100 text-green-700 border-green-200",
  Red:     "bg-red-100 text-red-700 border-red-200",
  Black:   "bg-zinc-900 text-zinc-100 border-zinc-700",
};

export const ALERT_DOT_COLORS: Record<AlertTier, string> = {
  Healthy: "bg-zinc-300",
  Green:   "bg-green-500",
  Red:     "bg-red-500",
  Black:   "bg-zinc-900",
};

export const ALERT_TIER_LABEL: Record<AlertTier, string> = {
  Healthy: "Stocked",
  Green:   "Green Zone — 80% consumed",
  Red:     "Red Zone — 90% consumed",
  Black:   "Black Zone — depleted",
};
